import React, { useEffect, useRef, useState } from 'react';
import './PlaylistsPage.css';
import './SupportPage.css';

import mobileNoise from './bg_noise/mobile_noise.gif';
import tabletNoise from './bg_noise/tablet_noise.gif';
import desktopNoise from './bg_noise/desktop_noise.gif';
import './osci-logo-engine-v26-transparent';
import { LOGO_SEQUENCE, OSCI_SVG_ASSET_URLS, getInitialLogoVisualIndexForPage } from './logoVisuals';

import instagramIcon from './soc icons/1_icons8-instagram.svg';
import spotifyProfileIcon from './soc icons/2_icons8-spotify.svg';
import youtubeSocialIcon from './soc icons/3_icons8-youtube.svg';
import pinterestIcon from './soc icons/5_icons8-pinterest.svg';
import homeIcon from './soc icons/0_home.svg';
import { applyRandomFlicker, decodeTextLocked, runAfterFontsReady } from './utils/textEffects';
import { useRegionalVisibility } from './regionalVisibility';

const DEFAULT_HERO_GIF = `${process.env.PUBLIC_URL}/static/img/Skull_scanline.gif`;

const SupportThankYouPage = () => {
  const { hideInstagram } = useRegionalVisibility();
  const [currentVisualIndex] = useState(() => getInitialLogoVisualIndexForPage('/support/thank-you'));
  const osciCanvasRef = useRef(null);
  const osciAnimatorRef = useRef(null);
  const osciSvgListRef = useRef([]);
  const [osciReady, setOsciReady] = useState(false);
  const currentVisual = LOGO_SEQUENCE[currentVisualIndex] ?? LOGO_SEQUENCE[0];

  useEffect(() => {
    const cleanups = [];
    let isMounted = true;

    const startEffects = () => {
      if (!isMounted) {
        return;
      }

      const decodeNodes = document.querySelectorAll('[data-decode]');
      const flickerNodes = document.querySelectorAll('.flicker-text');

      decodeNodes.forEach((node) => {
        const text = node.getAttribute('data-decode') || node.innerText;
        cleanups.push(decodeTextLocked(node, text));
      });

      flickerNodes.forEach((node) => {
        cleanups.push(applyRandomFlicker(node, 50, 200, 1, 0.7));
      });
    };

    const cancelFontWait = runAfterFontsReady(startEffects);

    return () => {
      isMounted = false;
      cancelFontWait();
      cleanups.forEach((cleanup) => cleanup && cleanup());
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadSvgAssets = async () => {
      try {
        const svgUrls = OSCI_SVG_ASSET_URLS;

        if (!svgUrls.length) {
          return;
        }

        const svgTexts = await Promise.all(svgUrls.map((url) => fetch(url).then((res) => res.text())));
        if (isMounted) {
          osciSvgListRef.current = svgTexts;
          setOsciReady(true);
        }
      } catch (error) {
        console.error('Failed to load osci SVG assets for support thank-you page', error);
      }
    };

    loadSvgAssets();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    const visual = LOGO_SEQUENCE[currentVisualIndex];
    if (visual.type !== 'osci') {
      if (osciAnimatorRef.current) {
        osciAnimatorRef.current.destroy();
        osciAnimatorRef.current = null;
      }
      return undefined;
    }
    if (!osciReady || !window.OsciLogoAnimator) {
      return undefined;
    }

    const canvas = osciCanvasRef.current;
    if (!canvas) {
      return undefined;
    }

    const animator = new window.OsciLogoAnimator({
      canvas,
      svgList: osciSvgListRef.current.length ? osciSvgListRef.current : undefined,
      svgIndex: 0,
      cycleOnRelock: true,
      direction: -1,
      beams: 3,
      transparent: true,
      trailFade: 0.1,
      background: 'rgba(0,0,0,0)',
      beamColor: '#ff0000',
      baseOutlineColor: '#0011ff',
      scan: false,
      vig: 0,
      rgbAberration: 20,
      speed: 1.55,
      speedBoost: 0.5,
      intensity: 2.2,
      highlightWidthPx: 60,
      maxFps: 60,
      renderScale: 0.99,
      quality: 1.0,
      adaptive: false,
      traceStepPx: 5,
      strokePx: 0.5,
      glowPx: 0,
      miterLimit: 14,
      baseOutlineAlpha: 0.1,
      baseOutlineStrokePx: 0.1,
      frameJitter: 0,
      stableJitterPx: 0,
      lostJitterPx: 0,
      dropout: 0.3,
      signalEnabled: true,
      signalNoise: 1,
      signalHoldMin: 3.2,
      signalHoldMax: 4.4,
      signalDropMin: 0.77,
      signalDropMax: 2.28,
      glitch: true,
      glitchChance: 0.37,
    });

    osciAnimatorRef.current = animator;
    animator.start();

    return () => {
      animator.destroy();
      osciAnimatorRef.current = null;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
      }
    };
  }, [currentVisualIndex, osciReady]);

  return (
    <div className="App playlists-page support-page support-thank-you-page">
      <div id="background">
        <img src={mobileNoise} alt="" aria-hidden="true" className="bg-gif mobile" />
        <img src={tabletNoise} alt="" aria-hidden="true" className="bg-gif tablet" />
        <img src={desktopNoise} alt="" aria-hidden="true" className="bg-gif desktop" />
        <img src={DEFAULT_HERO_GIF} alt="" aria-hidden="true" id="animated-gif" />
      </div>

      <div id="content-wrapper" className="playlists-wrapper">
        <div className="playlists-panel support-panel">
          <a href="/" className="logo-home-link" aria-label="Back to home">
            {currentVisual.type === 'image' ? (
              <img src={currentVisual.src} alt="IVA Gonzo logo" id="background-gif" />
            ) : (
              <div id="background-gif" className="osci-wrap">
                {osciReady ? (
                  <canvas id="osci-support-thank-you" ref={osciCanvasRef} />
                ) : (
                  <div className="osci-loading">loading…</div>
                )}
              </div>
            )}
          </a>

          <header className="playlists-header support-header">
            <p className="header-eyebrow">THANK YOU FOR THE SUPPORT</p>
            <h1 className="page-title flicker-text no-fade" data-decode="SUPPORT RECEIVED">
              SUPPORT RECEIVED
            </h1>
            <p className="page-subtitle">PROJECT • PLAYLISTS • INDIE ARTISTS</p>
            <p className="page-description">
              Your support helps grow the project, push the playlists further, and create more room for independent
              artists and future releases. If you left a note with your support, I will see it.
            </p>
          </header>

          <section className="support-section support-thank-you-actions">
            <div className="support-thank-you-actions__grid">
              <a className="support-thank-you-cta" href="/support">
                <span className="btn-glass" aria-hidden="true"></span>
                <span className="support-thank-you-cta__label">Back to support</span>
              </a>
              <a className="support-thank-you-cta" href="/playlists">
                <span className="btn-glass" aria-hidden="true"></span>
                <span className="support-thank-you-cta__label">Open playlists</span>
              </a>
            </div>
            <div className="support-compact-note">
              <p>Support remains separate from submission decisions, playlist placement, reviews, streams, or outcomes.</p>
            </div>
          </section>
        </div>

        <footer id="social-icons">
          <a href="/" aria-label="Back to home">
            <img className="social-icon" src={homeIcon} alt="Home" />
          </a>
          {!hideInstagram && (
            <a href="https://www.instagram.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
              <img className="social-icon" src={instagramIcon} alt="Instagram" />
            </a>
          )}
          <a
            href="https://open.spotify.com/user/11185646448?si=e1a94395b1054a48"
            target="_blank"
            rel="noopener noreferrer"
          >
            <img className="social-icon" src={spotifyProfileIcon} alt="Spotify" />
          </a>
          <a href="https://youtube.com/@ivagonzo/" target="_blank" rel="noopener noreferrer">
            <img className="social-icon" src={youtubeSocialIcon} alt="YouTube" />
          </a>
          <a href="https://www.pinterest.com/ivagonzo/" target="_blank" rel="noopener noreferrer">
            <img className="social-icon" src={pinterestIcon} alt="Pinterest" />
          </a>
        </footer>
      </div>
    </div>
  );
};

export default SupportThankYouPage;
